import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Loader from '../common/Loader.js';
import Button from '../common/Button.js';
import authService from '../../services/authService';
import { Eye, EyeOff, Mail, Lock, KeyRound, ArrowLeft } from 'lucide-react';
import AuthLayout, { authInputClass, authIconClass, authLabelClass, authPrimaryBtnClass, authOutlineLinkClass, authEyeBtnClass } from './AuthLayout.js';
import { useTranslation } from 'react-i18next';


const ResetPassword = () => {
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const savedEmail = sessionStorage.getItem('resetEmail');
    if (savedEmail) setEmail(savedEmail);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (otp.length !== 6) {
      setError('OTP must be 6 digits');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);

    try {
      const response = await authService.resetPassword(
        email,
        otp,
        newPassword,
        confirmPassword
      );

      setMessage(response.message || 'Password reset successfully');

      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Show loader when resetting password
  if (loading) {
    return <Loader fullScreen={true} size="default" message="Resetting your password..." />
  }

  return (
    <AuthLayout title="Reset Password" subtitle="Enter OTP and choose a new password">
      {/* ERROR MESSAGE */}
      {error && (
        <div className="mb-5 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
          {error}
        </div>
      )}

      {/* SUCCESS MESSAGE */}
      {message && (
        <div className="mb-5 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-sm">
          {message}
          <p className="text-xs mt-1 text-green-600">
            Redirecting to login...
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* EMAIL INPUT */}
        <div>
          <label className={authLabelClass}>Email Address</label>
          <div className="relative">
            <Mail className={authIconClass} strokeWidth={1.75} />
            <input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`${authInputClass} pl-12 pr-4`}
              required
              disabled={loading}
            />
          </div>
        </div>

        {/* OTP INPUT */}
        <div>
          <label className={authLabelClass}>OTP Code</label>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="000000"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className={`${authInputClass} px-4 text-center !text-lg tracking-[0.5em] font-semibold`}
            required
            disabled={loading}
            maxLength={6}
          />
        </div>

        {/* NEW PASSWORD INPUT WITH EYE ICON */}
        <div>
          <label className={authLabelClass}>New Password</label>
          <div className="relative">
            <Lock className={authIconClass} strokeWidth={1.75} />
            <input
              type={showNewPassword ? 'text' : 'password'}
              placeholder="Enter new password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className={`${authInputClass} pl-12 pr-12`}
              required
              disabled={loading}
            />
            <Button
              variant="ghost"
              size="sm"
              icon={showNewPassword ? EyeOff : Eye}
              onClick={() => setShowNewPassword(!showNewPassword)}
              className={authEyeBtnClass}
            />
          </div>
        </div>

        {/* CONFIRM PASSWORD INPUT WITH EYE ICON */}
        <div>
          <label className={authLabelClass}>Confirm Password</label>
          <div className="relative">
            <Lock className={authIconClass} strokeWidth={1.75} />
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={`${authInputClass} pl-12 pr-12`}
              required
              disabled={loading}
            />
            <Button
              variant="ghost"
              size="sm"
              icon={showConfirmPassword ? EyeOff : Eye}
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className={authEyeBtnClass}
            />
          </div>
        </div>

        {/* RESET PASSWORD BUTTON */}
        <Button
          variant="primary"
          type="submit"
          fullWidth
          loading={loading}
          size="lg"
          icon={KeyRound}
          className={authPrimaryBtnClass}
        >
          {loading ? 'RESETTING PASSWORD...' : 'RESET PASSWORD'}
        </Button>

        {/* BACK TO LOGIN */}
        <div className="pt-1">
          <Link to="/login" className={authOutlineLinkClass}>
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </Link>
        </div>

        <p className="pt-2 text-[11px] leading-relaxed text-slate-400 text-center">
          By continuing, you accept our{' '}
          <span className="text-blue-600">Terms &amp; Conditions</span> and{' '}
          <span className="text-blue-600">Privacy Policy</span>.
        </p>
      </form>
    </AuthLayout>
  );
};


export default ResetPassword;